import React, { useMemo, useState } from 'react';
import useSWR from 'swr';
import api from '../../services/api';
import { formatDateTime } from '../../config/appConfig';
import { useTheme, type ThemeColors } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { useSyncLogsLiveRefresh } from './useSyncLogsLiveRefresh';
import EmailLogsToolbar, { type EmailAccountOption, type StatusCounts, type SyncLogStatusFilter } from './components/EmailLogsToolbar';
/** Single step in the connection/sync log */
export interface ConnectionLogEntry {
  at: string;
  level: 'info' | 'warn' | 'error';
  message: string;
}

export interface EmailSyncLogEntry {
  id: number;
  emailAccountId: number;
  status: 'started' | 'completed' | 'error' | 'warning';
  message: string;
  details?: {
    emailsFound?: number;
    emailsSaved?: number;
    emailsFailed?: number;
    errors?: Array<{ messageId?: string; error: string }>;
    warnings?: string[];
  };
  emailsFound: number;
  emailsSaved: number;
  emailsFailed: number;
  duration?: number;
  errorMessage?: string;
  createdAt: string;
  logEntries?: ConnectionLogEntry[];
  emailAccount?: {
    id: number;
    emailAddress: string;
    displayName?: string | null;
  };
}

const POLL_INTERVAL_MS = 5_000;const LOG_LIMIT_OPTIONS = [50, 100, 200, 500];

const getStatusStyles = (status: string, colors: ThemeColors) => {
  switch (status) {
    case 'completed':
      return colors.success;
    case 'error':
      return colors.error;
    case 'warning':
      return colors.warning;
    case 'started':
      return colors.info;
    default:
      return cn(colors.surfaceHover, colors.textSecondary);
  }
};

const getStatusIcon = (status: string) => {
  switch (status) {
    case 'completed':
      return '✓';
    case 'error':
      return '✗';
    case 'warning':
      return '⚠';
    case 'started':
      return '⟳';
    default:
      return '•';
  }
};

const EmailLogsPage: React.FC = () => {
  const { colors } = useTheme();
  const [limit, setLimit] = useState(100);
  const [accountId, setAccountId] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<SyncLogStatusFilter>('');  const [liveEnabled, setLiveEnabled] = useState(true);
  const [expandedId, setExpandedId] = useState<number | null>(null);

  useSyncLogsLiveRefresh(liveEnabled);

  const query = useMemo(() => {
    const params = new URLSearchParams();
    params.set('limit', String(limit));
    if (accountId) params.set('accountId', accountId);
    return params.toString();
  }, [limit, accountId]);

  const { data: logs = [], isLoading, isValidating, mutate } = useSWR<EmailSyncLogEntry[]>(
    `/emails/sync-logs?${query}`,
    async (url: string) => {
      const res = await api.get(url);
      return res.data;
    },
    {
      refreshInterval: liveEnabled ? POLL_INTERVAL_MS : 0,
      refreshWhenHidden: false,
      revalidateOnFocus: liveEnabled,
      revalidateOnReconnect: liveEnabled,
      // Global SWR deduping is 5 min — must bypass or polling never hits the network.
      dedupingInterval: liveEnabled ? 0 : undefined,
      keepPreviousData: true,
    },
  );

  const { data: accounts = [] } = useSWR<EmailAccountOption[]>(    '/email-accounts/manage',
    async (url: string) => {
      const res = await api.get(url);
      return res.data;
    },
  );

  const filteredLogs = useMemo(() => {
    if (!statusFilter) return logs;
    return logs.filter((log) => log.status === statusFilter);
  }, [logs, statusFilter]);

  const statusCounts = useMemo<StatusCounts>(() => {
    const counts: StatusCounts = {
      all: logs.length,
      started: 0,
      completed: 0,
      warning: 0,
      error: 0,
    };
    for (const log of logs) {
      if (log.status === 'started') counts.started += 1;
      else if (log.status === 'completed') counts.completed += 1;
      else if (log.status === 'warning') counts.warning += 1;
      else if (log.status === 'error') counts.error += 1;
    }
    return counts;
  }, [logs]);

  const accountLabel = (log: EmailSyncLogEntry) => {
    const acc = log.emailAccount;
    if (!acc) return `Account #${log.emailAccountId}`;
    return acc.displayName || acc.emailAddress || `Account #${acc.id}`;
  };

  const lastUpdatedLabel = useMemo(() => {
    const newest = logs[0]?.createdAt;
    if (!newest) return null;
    return formatDateTime(newest);
  }, [logs]);

  return (
    <div className={cn('flex flex-col h-full min-h-0 p-4 md:p-6 gap-4', colors.background)}>
      <EmailLogsToolbar
        accounts={accounts}
        accountId={accountId}
        onAccountChange={setAccountId}
        statusFilter={statusFilter}
        onStatusChange={setStatusFilter}
        statusCounts={statusCounts}
        limit={limit}
        onLimitChange={setLimit}
        limitOptions={LOG_LIMIT_OPTIONS}
        liveEnabled={liveEnabled}
        onLiveChange={setLiveEnabled}
        pollIntervalSec={POLL_INTERVAL_MS / 1000}
        isValidating={isValidating}
        isLoading={isLoading}
        onRefresh={() => void mutate()}
        lastUpdatedLabel={lastUpdatedLabel}
        showingCount={filteredLogs.length}
      />

      <div className="flex flex-col flex-1 min-h-0">
        {isLoading && !logs.length ? (
          <div className={cn('flex flex-1 flex-col items-center justify-center py-16 rounded-xl border', colors.surface, colors.border, colors.textMuted)}>
            <div className="w-10 h-10 border-2 border-current border-t-transparent rounded-full animate-spin" />
            <p className="mt-3 text-sm">Loading logs...</p>
          </div>
        ) : filteredLogs.length === 0 ? (
          <div className={cn('flex flex-1 flex-col items-center justify-center py-16 rounded-xl border', colors.surface, colors.border, colors.textMuted)}>
            <p>No sync logs found.</p>
            <p className="text-sm mt-1">Logs will appear here when email sync runs.</p>
          </div>
        ) : (
          <div className={cn('flex flex-col flex-1 min-h-0 rounded-xl border overflow-hidden', colors.surface, colors.border)}>
            <div className="flex-1 min-h-0 overflow-auto dashboard-scrollbar">
              <table className="w-full text-sm">                <thead className={cn('sticky top-0 z-10 border-b', colors.surfaceHover, colors.border)}>
                  <tr>
                    <th className={cn('text-left py-3 px-4 font-semibold', colors.text)}>Time</th>
                    <th className={cn('text-left py-3 px-4 font-semibold', colors.text)}>Account</th>
                    <th className={cn('text-left py-3 px-4 font-semibold', colors.text)}>Status</th>
                    <th className={cn('text-left py-3 px-4 font-semibold', colors.text)}>Message</th>
                    <th className={cn('text-right py-3 px-4 font-semibold', colors.text)}>Duration</th>
                    <th className={cn('text-right py-3 px-4 font-semibold', colors.text)}>Found / Saved / Failed</th>
                    <th className="w-8 py-3 px-4" />
                  </tr>
                </thead>
                <tbody>
                  {filteredLogs.map((log) => {
                    const hasDetail =
                      (log.logEntries && log.logEntries.length > 0) ||
                      log.errorMessage ||
                      (log.details?.errors && log.details.errors.length > 0) ||
                      (log.details?.warnings && log.details.warnings.length > 0);
                    const isExpanded = expandedId === log.id;
                    return (
                      <React.Fragment key={log.id}>
                        <tr
                          className={cn(
                            'border-b last:border-b-0',
                            colors.border,
                            hasDetail && 'cursor-pointer',
                          )}
                          onClick={() => hasDetail && setExpandedId(isExpanded ? null : log.id)}
                        >
                          <td className={cn('py-2.5 px-4 whitespace-nowrap', colors.textMuted)}>
                            {formatDateTime(log.createdAt)}
                          </td>
                          <td className={cn('py-2.5 px-4', colors.text)}>{accountLabel(log)}</td>
                          <td className="py-2.5 px-4">
                            <span
                              className={cn(
                                'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border',
                                getStatusStyles(log.status, colors),
                              )}
                            >
                              {getStatusIcon(log.status)} {log.status}
                            </span>
                          </td>
                          <td className={cn('py-2.5 px-4 max-w-md truncate', colors.text)} title={log.message}>
                            {log.message}
                          </td>
                          <td className={cn('py-2.5 px-4 text-right whitespace-nowrap', colors.textMuted)}>
                            {log.duration != null ? `${(log.duration / 1000).toFixed(2)}s` : '–'}
                          </td>
                          <td className={cn('py-2.5 px-4 text-right whitespace-nowrap', colors.text)}>
                            {log.emailsFound > 0 || log.emailsSaved > 0 || log.emailsFailed > 0
                              ? `${log.emailsFound} / ${log.emailsSaved} / ${log.emailsFailed}`
                              : '–'}
                          </td>
                          <td className="py-2.5 px-4 w-8">
                            {hasDetail ? (
                              <span className={cn('text-xs', colors.textMuted)}>
                                {isExpanded ? '▼' : '▶'}
                              </span>
                            ) : null}
                          </td>
                        </tr>
                        {hasDetail && isExpanded && (
                          <tr className={cn('border-b', colors.border)}>
                            <td colSpan={7} className={cn('py-3 px-4', colors.surfaceHover)}>
                              <div className="space-y-4 text-sm">
                                {log.logEntries && log.logEntries.length > 0 && (
                                  <div>
                                    <span className={cn('font-semibold', colors.text)}>Connection log</span>
                                    <ul className="mt-2 space-y-1 max-h-64 overflow-y-auto">
                                      {log.logEntries.map((entry, idx) => (
                                        <li
                                          key={idx}
                                          className={cn(
                                            'flex items-start gap-2 text-xs font-mono',
                                            entry.level === 'error' && colors.error,
                                            entry.level === 'warn' && colors.warning,
                                          )}
                                        >
                                          <span className={cn('shrink-0 whitespace-nowrap', colors.textMuted)}>
                                            {formatDateTime(entry.at)}
                                          </span>
                                          <span
                                            className={cn(
                                              'shrink-0 w-10 text-right font-semibold uppercase',
                                              entry.level === 'error' && colors.error,
                                              entry.level === 'warn' && colors.warning,
                                              entry.level === 'info' && colors.textMuted,
                                            )}
                                          >
                                            {entry.level}
                                          </span>
                                          <span className={cn('min-w-0 break-words', colors.text)}>{entry.message}</span>
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                )}
                                {log.errorMessage && (
                                  <div>
                                    <span className={cn('font-semibold', colors.text)}>Error</span>
                                    <pre className={cn('mt-1 p-2 rounded overflow-x-auto text-xs font-mono', colors.surface, colors.border)}>
                                      {log.errorMessage}
                                    </pre>
                                  </div>
                                )}
                                {log.details?.errors && log.details.errors.length > 0 && (
                                  <div>
                                    <span className={cn('font-semibold', colors.text)}>Email errors</span>
                                    <ul className="mt-1 space-y-1">
                                      {log.details.errors.slice(0, 10).map((err, idx) => (
                                        <li key={idx} className={cn('font-mono text-xs p-1.5 rounded', colors.surface, colors.border)}>
                                          {err.messageId ? `${err.messageId}: ` : ''}{err.error}
                                        </li>
                                      ))}
                                      {log.details.errors.length > 10 && (
                                        <li className={cn('text-xs', colors.textMuted)}>
                                          ... and {log.details.errors.length - 10} more
                                        </li>
                                      )}
                                    </ul>
                                  </div>
                                )}
                                {log.details?.warnings && log.details.warnings.length > 0 && (
                                  <div>
                                    <span className={cn('font-semibold', colors.text)}>Warnings</span>
                                    <ul className="mt-1 list-disc list-inside space-y-0.5">
                                      {log.details.warnings.slice(0, 5).map((w, idx) => (
                                        <li key={idx} className={cn('text-xs', colors.text)}>{w}</li>
                                      ))}
                                      {log.details.warnings.length > 5 && (
                                        <li className={cn('text-xs', colors.textMuted)}>
                                          ... and {log.details.warnings.length - 5} more
                                        </li>
                                      )}
                                    </ul>
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
export default EmailLogsPage;

